import values from 'lodash/values';
import { useSearchParams } from 'next/navigation';
import P from 'prop-types';
import { useMemo } from 'react';
import { useDeviceType } from '~/app/lib/get-device-type';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import getEditingConfig from './editingConfig';
import KetoStructuredCard from './keto-structured-card';
import KetoUnstructuredCard from './keto-unstructured-card';
import { PRODUCT_TYPE_UNSTRUCTURED_VERTICAL } from './utils';

const arrayContainsArray = (superset, subset) => {
  return subset.every((value) => superset.includes(value));
};

const KetoProductCard = ({
  dataDependency,
  driver,
  isUnstructuredVertical,
  container,
  keyFacts,
  productCardImage,
  cardIdentifier,
  heading,
  analytics,
  ctaHref,
  ctaText,
  identifier,
  ctaSubtext,
  omitRating,
  omittedTabs,
  productType,
  starRating,
  includeWhyThisProduct,
  includeWhyNerdsLoveIt,
  shouldOutputCtaBelowKeyFacts,
  whyThisProductMethodologyUrl,
  whyThisProductText,
  whyThisProductTypeOverride,
  whyNerdsLoveItContainer,
  whyNerdsLoveItTitle,
  whyNerdsLoveItTitleIconUrl,
  whyNerdsLoveItText,
  whyNerdsLoveItMethodologyUrl,
  whyNerdsLoveItAvatarUrl,
  turnOnGroupedBorders,
  badgeText,
  includeBadge,
  filterByQueryParam,
  queryParamName,
  queryParamValue,
  driverOverrides: enableDriverOverrides,
  overrideProductName,
  overrideCtaText,
}) => {
  const footerOptions = {
    includeWhyThisProduct,
    includeWhyNerdsLoveIt,
    whyThisProductMethodologyUrl,
    whyThisProductText,
    whyThisProductTypeOverride,
    whyNerdsLoveItContainer,
    whyNerdsLoveItTitle,
    whyNerdsLoveItTitleIconUrl,
    whyNerdsLoveItText,
    whyNerdsLoveItMethodologyUrl,
    whyNerdsLoveItAvatarUrl,
    turnOnGroupedBorders,
  };

  const { pageViewId } = useAnalyticsPageView();
  const pageViewIdValue = useMemo(() => {
    return pageViewId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deviceType = useDeviceType();
  const searchParams = useSearchParams();
  const currentFilter = searchParams.get(queryParamName);

  const multiFilterKeys = queryParamName?.split('|').filter(Boolean);
  const multiFilterValues = queryParamValue?.split('|').filter(Boolean);

  const searchParamsEntries = useMemo(() => {
    const items = [];
    searchParams?.forEach((value, key) => {
      if (multiFilterKeys?.includes(key)) {
        items.push(value);
      }
    });
    return items;
  }, [multiFilterKeys, searchParams]);

  const shouldNotAppearWhenMultiLevelFiltered = useMemo(() => {
    if (!arrayContainsArray(multiFilterValues, searchParamsEntries)) {
      return true;
    }
    return false;
  }, [multiFilterValues, searchParamsEntries]);

  const shouldNotAppearWhenFiltered = useMemo(() => {
    if (filterByQueryParam && currentFilter) {
      const queryParamValues = queryParamValue?.split('|').filter(Boolean);
      if (!queryParamValues?.includes(currentFilter)) {
        return true;
      }
    }
    return false;
  }, [currentFilter, filterByQueryParam, queryParamValue]);

  const badgeProps = { badgeText: includeBadge && badgeText ? badgeText : '' };

  const baseKetoProps = {
    identifier,
    pageViewId: pageViewIdValue,
    analytics,
    omitRating,
    omittedTabs,
    badgeProps,
    keyFacts,
    shouldOutputCtaBelowKeyFacts,
    deviceType,
  };

  const unstructuredKetoProps = {
    productCardImage,
    cardIdentifier,
    heading,
    ctaHref,
    ctaText,
    ctaSubtext,
    productType,
    starRating,
  };

  const productOfferId = dataDependency?.data?.ids?.[0]?.id;

  const productOverride =
    overrideProductName || overrideCtaText
      ? {
          heading,
          ctaText,
        }
      : {};

  const driverOverrides = enableDriverOverrides ? keyFacts : [];

  if (shouldNotAppearWhenFiltered || shouldNotAppearWhenMultiLevelFiltered) {
    return null;
  }

  const universalContainer = { ...container, jumpLinkId: identifier };

  if (isUnstructuredVertical) {
    return (
      <UniversalContainer {...universalContainer}>
        <KetoUnstructuredCard
          footerOptions={footerOptions}
          {...{ ...baseKetoProps, ...unstructuredKetoProps }}
        />
      </UniversalContainer>
    );
  }

  if (productOfferId) {
    return (
      <UniversalContainer {...universalContainer}>
        <KetoStructuredCard
          pageViewId={pageViewIdValue}
          badgeProps={badgeProps}
          productOverride={productOverride}
          dataDependency={dataDependency}
          footerOptions={footerOptions}
          driverOverrides={driverOverrides}
          shouldOmitRating={omitRating}
          driver={driver}
          deviceType={deviceType}
        />
      </UniversalContainer>
    );
  }

  return null;
};

KetoProductCard.getEditingConfig = getEditingConfig;

KetoProductCard.propTypes = {
  dataDependency: P.shape({
    data: P.shape({ ids: P.arrayOf(P.string) }),
    type: P.string,
  }),
  isUnstructuredVertical: P.bool,
  container: universalContainerPropTypes,
  keyFacts: P.arrayOf(P.string),
  productCardImage: P.string,
  cardIdentifier: P.object,
  heading: P.string,
  badgeText: P.string,
  ctaHref: P.string,
  ctaSubtext: P.string,
  ctaText: P.string,
  segmentImpressionId: P.string,
  pageViewId: P.string,
  identifier: P.string,
  omitRating: P.bool,
  omittedTabs: P.bool,
  productType: P.oneOf(values(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL)),
  starRating: P.string,
  includeWhyThisProduct: P.bool,
  includeWhyNerdsLoveIt: P.bool,
  analytics: P.object,
  turnOnGroupedBorders: P.bool,
  shouldOutputCtaBelowKeyFacts: P.bool,
  whyThisProductMethodologyUrl: P.string,
  whyThisProductText: P.string,
  whyThisProductTypeOverride: P.string,
  whyNerdsLoveItContainer: P.string,
  whyNerdsLoveItTitle: P.string,
  whyNerdsLoveItTitleIconUrl: P.string,
  whyNerdsLoveItText: P.string,
  whyNerdsLoveItMethodologyUrl: P.string,
  whyNerdsLoveItAvatarUrl: P.string,
  driverOverrides: P.bool,
  driver: P.string,
  filterByQueryParam: P.string,
  queryParamName: P.string,
  queryParamValue: P.string,
  overrideProductName: P.bool,
  overrideCtaText: P.bool,
  includeBadge: P.bool,
  deviceType: P.string,
};

export default KetoProductCard;
